import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { H2 } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';

import AuctionVideoStream from '@/pages/BiddingConsole/components/AuctionVideoStream/AuctionVideoStream';
import MessageScroller from '@/pages/BiddingConsole/components/MessageScroller/MessageScroller';
import { useAppSelector } from '@/redux/hooks';
import { getCatalogTitle } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getLiveCatalogCurrentItemId } from '@/redux/modules/console';
import { getLocale } from '@/redux/modules/intl';
import { getItemSummaryLotNumber, getItemSummaryTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import { hasVideoStream } from '@/redux/modules/video';
import { popupCenter } from '@/utils/popupCenter';
import { getAuctionVideoUrl } from '@/utils/urls';

import AuctionLotsScroller from './components/AuctionLotsScroller/AuctionLotsScroller';
import AuctionLotTracker from './components/AuctionLotTracker/AuctionLotTracker';
import AuctionNameAndSettingsBlock from './components/AuctionNameAndSettingsBlock/AuctionNameAndSettingsBlock';
import CurrentItemDetails from './components/CurrentItemDetails/CurrentItemDetails';
import DesktopBidContainer from './components/DesktopBiddingContainer/DesktopBidContainer';

type Props = {
    catalogId: number;
};

const WIDTH = 1024;
const HEIGHT = 576;

const DesktopBiddingConsole = ({ catalogId }: Props) => {
    const windowRef = React.useRef(null);
    let timerRef = React.useRef<ReturnType<typeof setInterval>>(null);
    const [videoPoppedOut, setVideoPoppedOut] = React.useState(false);
    const locale = useAppSelector(getLocale);
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const itemId = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const liveItemTitle = useAppSelector((state) => getItemSummaryTitle(state, itemId));
    const liveItemLotNumber = useAppSelector((state) => getItemSummaryLotNumber(state, itemId));

    const localePath = locale === 'en-gb' ? `/${locale}` : '';
    const [selectedCurrency, setSelectedCurrency] = React.useState<string>();
    const catalogHasVideoStream = useAppSelector((state) => hasVideoStream(state, catalogId));

    const openVideoConsole = React.useCallback(() => {
        windowRef.current = popupCenter({
            height: HEIGHT,
            title: `${title} Video`,
            url: localePath + getAuctionVideoUrl(catalogId),
            width: WIDTH,
        });
        setVideoPoppedOut(true);

        timerRef.current = setInterval(function () {
            // Apparently this is the standard security work around for keeping track of whether a newly opened window has closed
            if (windowRef.current.closed) {
                clearInterval(timerRef.current);
                setVideoPoppedOut(false);
            }
        }, 1000);
    }, [catalogId, localePath, title]);
    return (
        <div className="mx-auto max-w-[1500px]">
            <div className="grid h-screen grid-cols-[372px_1fr]">
                <section className="flex flex-col bg-background-subdued px-md py-sm">
                    <AuctionNameAndSettingsBlock
                        catalogId={catalogId}
                        onCurrencyChanged={setSelectedCurrency}
                        selectedCurrency={selectedCurrency}
                    />
                    <AuctionLotTracker catalogId={catalogId} />
                    <AuctionLotsScroller catalogId={catalogId} />
                </section>
                <section>
                    <div className="grid h-screen grid-cols-[1fr_220px] grid-rows-[min-content_auto_min-content] [@media(min-width:1101px)]:[grid-template-columns:1fr_340px] [@media(min-width:1401px)]:[grid-template-columns:1fr_444px]">
                        <section className="col-span-2 row-start-1 mx-md bg-background-primary pb-sm pt-lg capitalize transition-all duration-200 ease-in">
                            <H2>
                                <FormattedMessage
                                    id="number_and_title"
                                    values={{
                                        lotNumber: liveItemLotNumber,
                                        title: liveItemTitle,
                                    }}
                                />
                            </H2>
                        </section>
                        <CurrentItemDetails
                            catalogId={catalogId}
                            className="col-start-1 row-start-2 pr-md"
                        />
                        <DesktopBidContainer
                            className="col-span-2 row-start-3 transition-all duration-200 ease-in"
                            itemId={itemId}
                            selectedCurrency={selectedCurrency}
                        />
                        <section className="relative col-start-2 row-start-2 mb-sm mr-md flex flex-col overflow-hidden border border-border-secondary bg-background-primary transition-all duration-200 ease-in [@media(min-height:1500px)]:border-l [@media(min-height:1500px)]:border-border-secondary">
                            {catalogHasVideoStream && !videoPoppedOut && (
                                <AuctionVideoStream
                                    catalogId={catalogId}
                                    className="z-[1] flex-none"
                                    onOpenInNewWindow={openVideoConsole}
                                />
                            )}
                            <MessageScroller
                                catalogId={catalogId}
                                convert={false}
                                hideMessages={false}
                            />
                        </section>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default DesktopBiddingConsole;
